import { AskUs } from "@component/Widget/AskUs/AskUs";
import SearchComp from "@component/Widget/SearchForm/SearchComp";
import { faMagnifyingGlass as farMagnifyingGlass } from "@awesome.me/kit-3777c2145e/icons/classic/regular";
import {
	faBabyCarriage as fasBabyCarriage,
	faFileInvoiceDollar as fasFileInvoiceDollar,
	faHouseDay as fasHouseDay,
	faHouse as fasHouse,
	faMoneyBill as fasMoneyBill,
	faCircleQuestion as fasCircleQuestion,
	faPhone as fasPhone,
} from "@awesome.me/kit-3777c2145e/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { logError } from "@utils/sentry-error";
import autobind from "autobind-decorator";
import { http, Button, HtmlBlock, InfoPopup, PromisePool, getUrlParameter, scrollToElement } from "bm-ui-kit";
import type { ReactNode } from "react";
import React from "react";
import { Accordion, Alert, Breadcrumb, Col, Container, InputGroup, Row } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import { LandingAccordion } from "./components/LandingAccordion";
import { LandingContainer } from "./components/LandingContainer";
import "./jobb-tudni.scss";

export type CategorySlug =
	| "babavaro"
	| "csok"
	| "lakashitel"
	| "szemelyi-kolcson"
	| "hitelkartya"
	| "lakastakarek"
	| "befektetesi-alap"
	| "bankbetet"
	| "bankszamla"
	| "allampapir"
	| "nyugdijmegtakaritas";

type CategoryItem = {
	name: string;
	slug: CategorySlug;
	icon: ReactNode;
};

interface Props {
	categorySlug: CategorySlug;
}

export interface State {
	deviceWidth: number;
	categorySlug: CategorySlug;
	items: Item[];
}

interface Post {
	id: number;
	slug: string;
	title: { rendered: string };
	content: { rendered: string };
}
interface Item {
	id: number;
	slug: string;
	title: string;
	content: string;
}

// NOTE: ha az a kategóriát bővíted, frissítsd a route-ot a src/js/landing/jobb-tudni-category.tsx-fájlban. PHP-vel is létre lehet hozni kategóriákat itt: extra/post-extras.php (kategóriák feltöltése rész)

const CATEGORIES: CategoryItem[] = [
	{
		name: "Babaváró",
		slug: "babavaro",
		icon: <FontAwesomeIcon icon={fasBabyCarriage} />,
	},
	{
		name: "Bankszámla",
		slug: "bankszamla",
		icon: <FontAwesomeIcon icon={fasFileInvoiceDollar} />,
	},
	{
		name: "CSOK",
		slug: "csok",
		icon: <FontAwesomeIcon icon={fasHouseDay} />,
	},
	{
		name: "Lakáshitel",
		slug: "lakashitel",
		icon: <FontAwesomeIcon icon={fasHouse} />,
	},
	{
		name: "Személyi kölcsön",
		slug: "szemelyi-kolcson",
		icon: <FontAwesomeIcon icon={fasMoneyBill} />,
	},
	// NOTE: kikommentelve míg nincs tartalmuk
	// {
	// 	name: 'Hitelkártya',
	// 	slug: 'hitelkartya',
	// icon: <FontAwesomeIcon icon={faCreditCard} />,
	// },
	// {
	// 	name: 'Lakástakarék',
	// 	slug: 'lakastakarek',
	// icon: <FontAwesomeIcon icon={faPiggyBank} />,
	// },
	// {
	// 	name: 'Befektetési alap',
	// 	slug: 'befektetesi-alap',
	// icon: <FontAwesomeIcon icon={faChartLine} />,
	// },
	// {
	// 	name: 'Bankbetét',
	// 	slug: 'bankbetet',
	// icon: <FontAwesomeIcon icon={faUniversity} />,
	// },
	// {
	// 	name: 'Állampapír',
	// 	slug: 'allampapir',
	// icon: <FontAwesomeIcon icon={faFileCertificate} />,
	// },
	// {
	// 	name: 'Nyugdíjmegtakarítás',
	// 	slug: 'nyugdijmegtakaritas',
	// icon: <FontAwesomeIcon icon={faIslandTropical} />,
	// },
];

/**
 * @Routing
 *
 * @title "Jobb tudni"
 * @description "Jobb tudni"
 * @route "/jobb-tudni/"
 */
export default class Landing extends React.Component<Props, State> {
	private readonly promisePool = new PromisePool();

	protected showResult = this.getHash() === "all";

	public constructor(props: Props) {
		super(props);
		this.state = this.getInitialState();
	}

	protected getInitialState(): State {
		const { categorySlug = null } = this.props;

		return {
			deviceWidth: this.getDeviceWidth(),
			categorySlug,
			items: null,
		};
	}

	public componentDidMount(): void {
		this.checkMobileMenu();
		this.loadPostByCategory();
		window.addEventListener("resize", this.checkMobileMenu, { passive: true });
	}

	public componentWillUnmount(): void {
		window.removeEventListener("resize", this.checkMobileMenu);
		this.promisePool.cancel();
	}

	public render(): ReactNode {
		const { categorySlug } = this.state;
		const title = CATEGORIES.find(({ slug }) => slug === categorySlug)?.name;

		return (
			<>
				{this.renderHeader()}
				{this.showResult && (
					<Container>
						{this.renderBreadcrumbs(title)}
						<div className="mb-5 mt-md-4">
							<div className="display-4 font-weight-bold mb-3 pt-md-2">{title}</div>
							{this.renderList()}
						</div>
					</Container>
				)}
			</>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<LandingContainer
				bgClassName="bg-trianglify-purple bg-border-bottom"
				className="text-white py-4"
				bgColor="#c33350"
				size="null"
			>
				<h1 className="display-1 display-bold pl-2">Jobb tudni tudástár</h1>
				<div className="display-6 mt-4">
					<strong>Bankmonitor</strong> szakértői által összeállított tudástár, ami pénzügyekkel kapcsolatos
					fogalmakat és
					<br className="d-none d-md-inline" /> magyarázatokat foglal össze.
				</div>

				<div className="pt-2 pb-3">
					<div className="d-none d-md-block">{this.renderCategories()}</div>
					{this.renderSearchForm()}
				</div>
			</LandingContainer>
		);
	}

	protected renderSearchForm(): ReactNode {
		return (
			<>
				<InfoPopup
					tooltip="Keresés a jobb tudni tudástárban"
					as="label"
					className="text-white font-weight-bold form-label"
				>
					Keresés a tartalomban
					<FontAwesomeIcon icon={fasCircleQuestion} className="ml-1" />
				</InfoPopup>
				<Row className="search-row">
					<Col md={6} lg={4}>
						<InputGroup>
							<SearchComp
								placeholder="keresett kifejezés..."
								subtypeFilter="bm_better_to_know"
								showAllUrl="/jobb-tudni/?f={search}#all"
							/>
							<InputGroup.Append onClick={() => this.focusSearchInput()}>
								<InputGroup.Text>
									<FontAwesomeIcon icon={farMagnifyingGlass} />
								</InputGroup.Text>
							</InputGroup.Append>
						</InputGroup>
					</Col>
				</Row>

				<div className="d-block d-md-none">{this.renderCategories()}</div>
				<Row>
					<Col lg={6}>
						<hr className="bg-white ml-0" />
					</Col>
				</Row>
				<p className="display-6">
					Amennyiben nem találtál valamilyen kérdésedre választ,{" "}
					<strong>
						kérd szakértő
						<br className="d-none d-md-inline" /> segítségét
					</strong>
					, és <strong>ingyenes</strong> segítséget nyújtunk!
				</p>
				<AskUs />
				<Button variant="gold" className="mt-3" icon={<FontAwesomeIcon icon={fasPhone} />} href="#askUsModal">
					Szakértő segítségét kérem!
				</Button>
			</>
		);
	}

	protected renderCategories(): ReactNode {
		return (
			<Row className="mt-4 mt-md-0 text-md-center no-gutters category-row">
				{CATEGORIES.map(({ name, slug, icon }, key) => (
					<Col lg={2} md={3} xs={6} className="px-1 mt-2 mb-4" key={`category:${key}`}>
						<a key={key} href={`/jobb-tudni/${slug}`} className="d-block">
							<span className="text-gold fa-2_5x">{icon}</span>
							<strong className="d-block mt-1 text-white text-uppercase">
								{this.formatLongName(slug, name)}
							</strong>
						</a>
					</Col>
				))}
			</Row>
		);
	}

	private renderBreadcrumbs(currentTitle: string): ReactNode {
		return (
			<Breadcrumb className="d-block d-md-none">
				<Breadcrumb.Item href="/">Bankmonitor</Breadcrumb.Item>
				<Breadcrumb.Item href="/jobb-tudni/">Jobb tudni</Breadcrumb.Item>
				<Breadcrumb.Item active>{currentTitle}</Breadcrumb.Item>
			</Breadcrumb>
		);
	}

	private renderList(): ReactNode {
		const { items } = this.state;
		const currentAccordionSlug = this.getHash();

		if (items === null) return <Skeleton count={10} height="40px" />;

		if (items?.length === 0) return <Alert variant="warning">Nincs találat.</Alert>;

		return (
			<Accordion className="accordion-secondary" defaultActiveKey={currentAccordionSlug}>
				{items.map(({ title, content, slug }) => (
					<LandingAccordion title={title} icon="" id={slug} key={slug}>
						<HtmlBlock html={content} id={`list_item_${slug}`} />
					</LandingAccordion>
				))}
			</Accordion>
		);
	}

	private loadPostByCategory(): void {
		const { categorySlug } = this.state;

		const sourceDOM = document.getElementById("better_to_know_source");

		if (sourceDOM !== null) {
			const items: Item[] = [];
			// get from DOM
			[...sourceDOM.querySelectorAll("article[data-id]")].forEach((sourceElement) => {
				const element = sourceElement as HTMLDivElement;
				items.push({
					id: Number(element.dataset.id),
					slug: element.dataset.slug,
					title: element.querySelector("h3").innerText,
					content: element.querySelector("div").innerHTML,
				});
			});
			this.setState(
				{
					items,
				},
				() => this.scrollToOpenedAccordion(),
			);
		} else {
			// load form api
			const search = getUrlParameter("f", "");
			this.promisePool
				.add(
					http.get(
						`/wp-json/wp/v2/bm_better_to_know/${
							this.getHash() !== "all"
								? `?bm_better_to_know_category_slug=${categorySlug}`
								: `?search=${search}`
						}`,
					),
				)
				.then((items: Post[]) => {
					this.setState(
						{
							items: items.map(({ id, slug, title, content }) => ({
								id,
								slug,
								title: title.rendered,
								content: content.rendered,
							})),
						},
						() => this.scrollToOpenedAccordion(),
					);
				})
				.catch((error: Error) => {
					// TODO: Hibakezelés
					logError(error);
				});
		}
	}

	private getHash(): string {
		return window.location.hash?.substr(1);
	}

	private scrollToOpenedAccordion(): void {
		const currentAccordionSlug = this.getHash();

		if (currentAccordionSlug !== "") {
			scrollToElement(`list_item_${currentAccordionSlug}`, { delay: 800 });
		}
	}

	protected formatLongName(slug: CategorySlug, name: string): string {
		const { deviceWidth } = this.state;

		// change long names
		if (slug === "nyugdijmegtakaritas" && (deviceWidth >= 992 || deviceWidth <= 445)) {
			return "Nyugdíj-megtakarítás";
		}

		return name;
	}

	private getDeviceWidth(): number {
		return window.innerWidth;
	}

	@autobind
	private checkMobileMenu(): void {
		const { deviceWidth } = this.state;
		const currentMobielView = this.getDeviceWidth();

		if (deviceWidth !== currentMobielView) {
			this.setState({
				deviceWidth: currentMobielView,
			});
		}
	}

	private focusSearchInput(): void {
		(document.querySelector(".search-row input.rbt-input-main") as HTMLInputElement).focus();
	}
}
